import { FormEvent, useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { restoreWorkflowRequest } from "@requests/service-configuration";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import defaultStyles from "../../common/modals/default-modal/default-modal.less";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { useDispatch } from "react-redux";
import { FormattedWorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { replaceWorkflowConfiguration } from "@redux/actions/workflows-actions";
import { ModalSizeTypes } from "@enums/modal-size-types";

type RestoreVerificationWorkflowModalProps = {
    onModalClose: () => void;
    workflowConfiguration: FormattedWorkflowConfiguration;
};

export const RestoreVerificationWorkflowModal = (props: RestoreVerificationWorkflowModalProps) => {
    const dispatch = useDispatch();
    const { onModalClose, workflowConfiguration } = props;

    const [requestInProgress, setRequestInProgress] = useState(false);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const { data, error } = await restoreWorkflowRequest(workflowConfiguration.id);
        if (data) {
            displaySuccessModal(
                dispatch,
                resources.requests.restoreWorkflow.success.title,
                resources.requests.restoreWorkflow.success.message
            );

            const newWorkflowConfiguration: FormattedWorkflowConfiguration = {
                ...workflowConfiguration,
                permissions: {
                    ...workflowConfiguration.permissions,
                    canArchive: true,
                    canRestore: false
                },
                status: EntityStatusTypes.ACTIVE
            };

            dispatch(replaceWorkflowConfiguration(newWorkflowConfiguration));
            onModalClose();
        } else {
            if (error) {
                logAndDisplayErrorModal(
                    dispatch,
                    error,
                    resources.requests.restoreWorkflow.error.title,
                    resources.requests.restoreWorkflow.error.message
                );
            }
            setRequestInProgress(false);
        }
    };

    return (
        <DefaultModal
            automationId="restore-verification-workflow-modal"
            ariaLabelledBy="restore-verification-workflow-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
            size={ModalSizeTypes.MEDIUM}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"restore-verification-workflow-title"} closeButton>
                    <Modal.Title>{resources.modals.restoreVerificationWorkflow.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {resources.modals.restoreVerificationWorkflow.description}
                    </span>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        disabled={requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.modals.restoreVerificationWorkflow.buttons.proceed.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};